import { Button, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import _ from "lodash";
import React, { Component } from "react";
import styled from "styled-components";

import { WorkspaceResourceType } from "common/interfaces";
import WorkspaceController, { getWorkspaceResourcesTags } from "js/controllers/WorkspaceController";
import { Slide, getSlideDataStateFromAttributes } from "js/core/models/slide";
import DummyAdapter from "js/core/storage/dummyAdapter";
import { htmlToText, sanitizeHtmlText } from "js/core/utilities/htmlTextHelpers";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import { BeautifulDialog, ShowDialog, ShowErrorDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import TagsList from "js/react/components/TagsList";
import { Field } from "js/react/components/UiComponents";

const NameField = styled(Field)`
    &.field {
        margin-bottom: 20px;
    }
`;

const TeamSlideTypeOptions = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 32px;
`;

const TeamSlideTypeOption = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 10px 12px 11px;
    min-height: 70px;
    background: ${props => props.$selected ? "#f4fbfe" : "#fafafa"};
    border: solid 1px ${props => props.$selected ? "#11a9e2" : "transparent"};
    border-radius: 2px;
    cursor: pointer;

    &:hover {
        background: ${props => props.$selected ? "#f4fbfe" : "#fafafa"};
        border-color: ${props => props.$selected ? "#11a9e2" : "#dedede"};
    }
`;

const TeamSlideTypeCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

const TeamSlideTypeTitle = styled.div`
    color: #333;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
`;

const TeamSlideTypeDescription = styled.div`
    color: #666;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.25;
`;

const StyledDialogContent = styled(DialogContent)`
    && {
        padding: 0 50px;
        overflow-y: auto;
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
    && {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
`;

const TitleText = styled.div`
    color: #333;
    font-size: 23px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0;
`;

const StyledDialogActions = styled(DialogActions)`
    && {
        padding-top: 18px;
    }
`;

export class CreateSharedSlideDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            name: props.initialName ?? "",
            description: props.initialDescription ?? "",
            tags: props.initialTags ?? [],
            slideType: props.resourceType ?? WorkspaceResourceType.SLIDE_TEMPLATE,
            fetching: false
        };
    }

    getSlideName = slide => {
        const slideDataState = getSlideDataStateFromAttributes(slide.attributes);
        if (!slideDataState?.elements.primary) {
            return "";
        }

        const state = slide.attributes.states[0];
        const templateId = slide.get("template_id");
        const primaryText = this.extractTextByPriority(state, ["primary"]);

        // Special handling for different slide types
        if (templateId === "headline2" || templateId === "section" || templateId === "headline") {
            const blocks = state.primary?.text?.blocks;

            if (Array.isArray(blocks)) {
                const priorityIds = ["headline", "heading", "title"];

                for (const id of priorityIds) {
                    //section has id but headline does not, so need to check textStyle
                    const block = blocks.find(b => b.textStyle === id);
                    const html = block?.html;

                    if (html) {
                        const text = htmlToText(html).trim();
                        if (text) {
                            return text;
                        }
                    }
                }
            }
            return "";
        } else if (templateId === "collage") {
            return this.extractTextByPriority(state, ["header"]);
        } else if (templateId === "title") {
            return this.extractTextByPriority(state, ["primary"]);
        } else if (templateId === "contactus") {
            const primaryItems = state.primary?.items;

            if (Array.isArray(primaryItems)) {
                const contactHeader = primaryItems.find(item => item.type === "contact_header");
                const html = contactHeader?.text?.blocks?.[0]?.html;

                if (html) {
                    const text = htmlToText(html).trim();
                    if (text) {
                        return text;
                    }
                }
            }
            return t("Contact Us");
        } else {
            const headerText = this.extractTextByPriority(state, ["header"]);
            if (headerText) {
                return headerText;
            }

            // If no header return blank
            return "";
        }
    }

    //Helper method to extract text by priority from text blocks
    extractTextByPriority = (state, blockTypes) => {
        for (const blockType of blockTypes) {
            if (state[blockType] && state[blockType].text && state[blockType].text.blocks) {
                const block = state[blockType].text.blocks.find(b => b.html);
                if (block && block.html) {
                    const text = htmlToText(block.html);
                    if (text && text.trim()) {
                        return text.trim();
                    }
                }
            } else if (state[blockType] && state[blockType].text) {
                if (typeof state[blockType].text === "string") {
                    const text = state[blockType].text.trim();
                    if (text) {
                        return text;
                    }
                }
            }
        }

        return "";
    }

    componentDidMount() {
        const { slide } = this.props;

        // Auto-populate slide name based on slide content
        if (slide && !this.props.initialName) {
            const extractedName = this.getSlideName(slide);
            if (extractedName) {
                this.setState({
                    name: extractedName
                });
            }
        }
    }

    handleAddTag = async () => {
        const existingTags = await getWorkspaceResourcesTags({ workspaceId: WorkspaceController.workspaceId, resourceTypes: [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE] });

        ShowDialog(AddTagsDialog, {
            activeTags: this.state.tags,
            existingTags: existingTags.map(tag => tag.tag),
            acceptCallback: newTags => {
                this.setState({
                    tags: newTags,
                });
            },
        });
    }

    handleRemoveTag = inputTag => {
        this.setState(({ tags }) => ({ tags: tags.filter(tag => tag !== inputTag) }));
    }

    handleConfirm = async () => {
        const { slide, presentation, onBeforeSaveSlide, closeDialog, folderId, forceCreateNewSlide, initialIsDisabled } = this.props;
        const { name, description, tags, slideType } = this.state;

        if (name.trim() === "") {
            return ShowErrorDialog({ title: t("Please enter a name for your team slide") });
        }

        try {
            this.setState({ fetching: true });

            let createdSlide;
            // If the slide is not saved or we are creating a Team Slide Template then will create and save a new slide
            if (forceCreateNewSlide || slide.adapter instanceof DummyAdapter || slideType === WorkspaceResourceType.SLIDE_TEMPLATE) {
                const slideAttributes = _.cloneDeep(slide.attributes);
                slideAttributes.isTeamSlideTemplate = slideType === WorkspaceResourceType.SLIDE_TEMPLATE;
                delete slideAttributes.id;
                delete slideAttributes.presentationId;
                delete slideAttributes.libraryItemId;
                if (presentation) {
                    slideAttributes.presentationId = presentation.id;
                }
                createdSlide = new Slide(slideAttributes, { userId: app.user.id, autoSync: false });
                await createdSlide.load();
            } else {
                createdSlide = slide;
            }

            // Before committing a slide, perform any updates
            onBeforeSaveSlide && onBeforeSaveSlide(createdSlide);

            // if we are sharing an existing slide as a TeamSlide, trigger a model change so it switches a TeamSlide in the current presentation
            // If updating an existing slide then we'll wait for the changes to propagate to the slide
            const slideUpdatePromise = createdSlide === slide
                ? new Promise(resolve => createdSlide.once("change:libraryItemId", resolve))
                : Promise.resolve();

            const resource = {
                resourceId: createdSlide.id,
                resourceType: slideType,
                tags,
                folderIds: [],
                metadata: {
                    name,
                    description,
                    isDisabled: initialIsDisabled ?? false,
                    contentModifiedAt: createdSlide.get("modifiedAt"),
                    usedByUsers: presentation ? [app.user.id] : [],
                    usedInPresentations: presentation ? [presentation.id] : [],
                    usedInPublicPresentations: presentation?.get("public") ? [presentation.id] : [],
                    ratedByUsers: [],
                    createdBy: app.user.id,
                    modifiedBy: app.user.id,
                }
            };

            await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });

            if (folderId) {
                await WorkspaceController.updateWorkspaceResourceFolderResources({
                    folderId,
                    update: {
                        add: [{ resourceId: createdSlide.id, resourceType: slideType, permissions: { read: { object: true, permissions: true }, write: { object: false, permissions: false } } }],
                    },
                });
            }

            // Wait for the changes to propagate (if needed)
            await slideUpdatePromise;

            closeDialog(slideType);
        } catch (err) {
            this.setState({ fetching: false });
            ShowWarningDialog({ title: t("An error occurred while sharing your slide"), message: err });
        }
    }

    handleSlideTypeChange = slideType => {
        this.setState({
            slideType,
        });
    }

    handleSlideTypeKeyDown = (event, slideType) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            this.handleSlideTypeChange(slideType);
        }
    }

    render() {
        const { closeDialog, dialogTitle, okButtonLabel } = this.props;
        const { name, description, tags, fetching, slideType } = this.state;
        const titleText = dialogTitle ?? t("Publish this slide?");

        return (
            <BeautifulDialog closeDialog={closeDialog} preventClose={fetching}>
                <FetchingClickShield visible={fetching} backgroundColor="rgba(255, 255, 255, 0.7)" />
                <StyledDialogTitle>
                    <TitleText>{titleText}</TitleText>
                </StyledDialogTitle>
                <StyledDialogContent>
                    <NameField title={t("Name")}>
                        <TextField
                            id="slide-name"
                            value={name}
                            placeholder={t("Name your Team Slide...")}
                            fullWidth
                            onChange={event => this.setState({ name: sanitizeHtmlText(event.target.value) })}
                        />
                    </NameField>
                    <TeamSlideTypeOptions aria-label={t("Team slide type")} role="radiogroup">
                        <TeamSlideTypeOption
                            $selected={slideType === WorkspaceResourceType.SLIDE_TEMPLATE}
                            aria-checked={slideType === WorkspaceResourceType.SLIDE_TEMPLATE}
                            onClick={() => this.handleSlideTypeChange(WorkspaceResourceType.SLIDE_TEMPLATE)}
                            onKeyDown={event => this.handleSlideTypeKeyDown(event, WorkspaceResourceType.SLIDE_TEMPLATE)}
                            role="radio"
                            tabIndex={0}
                        >
                            <TeamSlideTypeCopy>
                                <TeamSlideTypeTitle>{t("Slide Template")}</TeamSlideTypeTitle>
                                <TeamSlideTypeDescription>
                                    {t("Creates editable copies your team can customize.")}
                                </TeamSlideTypeDescription>
                            </TeamSlideTypeCopy>
                        </TeamSlideTypeOption>
                        <TeamSlideTypeOption
                            $selected={slideType === WorkspaceResourceType.SLIDE}
                            aria-checked={slideType === WorkspaceResourceType.SLIDE}
                            onClick={() => this.handleSlideTypeChange(WorkspaceResourceType.SLIDE)}
                            onKeyDown={event => this.handleSlideTypeKeyDown(event, WorkspaceResourceType.SLIDE)}
                            role="radio"
                            tabIndex={0}
                        >
                            <TeamSlideTypeCopy>
                                <TeamSlideTypeTitle>{t("Shared Slide")}</TeamSlideTypeTitle>
                                <TeamSlideTypeDescription>
                                    {t("Synced and uneditable. Team Librarians can make updates and it'll update everywhere it's used.")}
                                </TeamSlideTypeDescription>
                            </TeamSlideTypeCopy>
                        </TeamSlideTypeOption>
                    </TeamSlideTypeOptions>
                    <Field title={t("Description or instructions (optional)")}>
                        <TextField multiline
                            fullWidth
                            value={description}
                            placeholder={t("Give your team guidance on how and when to use this slide...")}
                            onChange={event => this.setState({ description: sanitizeHtmlText(event.target.value) })}
                            inputProps={{
                                onBlur: event => this.setState({ description: event.target.value })
                            }}
                        />
                    </Field>
                    <Field title={t("Tags (optional)")}>
                        <TagsList
                            tags={tags}
                            onRemove={this.handleRemoveTag}
                            onClick={this.handleAddTag}
                        />
                    </Field>
                </StyledDialogContent>

                <StyledDialogActions>
                    <Button id="cancelButton" onClick={() => closeDialog(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button id="okButton" onClick={this.handleConfirm} color="primary">
                        {okButtonLabel ?? t("Publish")}
                    </Button>
                </StyledDialogActions>
            </BeautifulDialog>
        );
    }
}

export default CreateSharedSlideDialog;
